import { Country, Language } from '@/models/Global'
import { ImgSize } from '@/models/Image'
import Message from '@/models/Message'
import FileStorageService from '@/services/FileStorageService'
import { useDefaultStore } from '@/store'
import { getCountryFlag } from '@/utils/tools'
import { getCurrentInstance, nextTick } from 'vue'
import { useTranslation } from './useTranslation'

export function useTools() {
  const store = useDefaultStore()
  const instance = getCurrentInstance()
  const { appTranslate } = useTranslation()

  // ---------------
  // -- Functions --
  // ---------------
  const getFlag = (country?: Country | 'world', language?: Language, style = 'shiny' as 'flat' | 'shiny') => {
    let countryFlag: Country | string | undefined = country

    if (!countryFlag && language) {
      countryFlag = store.mainLanguageCountries.get(language)
    }

    if (countryFlag) {
      return getCountryFlag(countryFlag, style)
    }
  }

  const confirmNav = (confirmMsg?: string) => {
    const userResponse = !store.confirmNavigationFrom || confirm(confirmMsg || 'Dina ändringar kommer inte sparas. Är du säker att du vill lämna sidan?')
    if (userResponse) {
      store.confirmNavigationFrom = undefined
    }
    return userResponse
  }

  const setTempImageUrl = async (imgElement: HTMLImageElement, imageName: string, size: ImgSize, title?: string) => {
    const url = await FileStorageService.getTempProductImageUrl(imageName, size)
    if (url) {
      imgElement.src = url
      if (title) {
        imgElement.parentElement?.setAttribute('data-title', title)
        // const label = document.createElement('div')
        // label.innerText = title
        // imgElement.parentElement?.appendChild(label)
      }
    }
    return url
  }

  const getRef = <T>(refString: string): T | undefined => {
    const ref = instance?.refs[refString]
    return ref && Array.isArray(ref) ? ref[0] : (ref as T | undefined)
  }

  const focusElement = (ref: string) => {
    const element = getRef<HTMLInputElement>(ref)
    setTimeout(() => {
      element?.focus()
    }, 100)
  }

  const getElementSize = (ref: string, property: 'bottom' | 'height' | 'left' | 'right' | 'top' | 'width' | 'x' | 'y') => {
    const element = getRef<HTMLElement>(ref)
    return element?.getBoundingClientRect()[property]
    // return size ? `${size}px` : undefined
  }

  const _setDropdownDirection = (anchorElement: HTMLElement | undefined, listElement: HTMLElement | undefined, additionalSpace = 10) => {
    nextTick(() => {
      if (anchorElement && listElement) {
        const bottomListPosition = window.scrollY + anchorElement.getBoundingClientRect().bottom + listElement.getBoundingClientRect().height + additionalSpace

        listElement.style.bottom = bottomListPosition > document.body.scrollHeight ? `${anchorElement.getBoundingClientRect().height + 1}px` : ''
      }
    })
  }

  const setDropdownDirection = (anchorElement?: HTMLElement, listElement?: HTMLElement, additionalSpace = 10) => {
    nextTick(() => {
      if (!anchorElement || !listElement) return

      const anchorRect = anchorElement.getBoundingClientRect()
      const listHeight = listElement.getBoundingClientRect().height
      const viewportHeight = window.innerHeight

      const spaceBelow = viewportHeight - anchorRect.bottom

      const openUpwards = spaceBelow - additionalSpace < listHeight

      if (openUpwards) {
        const adjustment = -listHeight - anchorRect.height - 2
        listElement.style.transform = `translateY(${adjustment}px)`
      }
    })
  }

  const getCurrency = (country: Country) => (country ? store.availableCulturesByCountry.get(country)?.currency : undefined)

  const addDefaultErrorMsg = (err: any) => {
    store.addMessage(new Message(appTranslate('An error occurred'), err.response?.data || err.message, 'danger'))
  }

  return {
    getFlag,
    confirmNav,
    setTempImageUrl,
    getRef,
    focusElement,
    getElementSize,
    setDropdownDirection,
    getCurrency,
    addDefaultErrorMsg,
  }
}
